import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ApiFailure, authApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { AuthShell } from '@/components/ui/AuthShell';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';

export function MfaVerifyPage() {
  const navigate = useNavigate();
  const challengeToken = useAuthStore((s) => s.challengeToken);
  const accessToken = useAuthStore((s) => s.accessToken);
  const mfaSetupRequired = useAuthStore((s) => s.mfaSetupRequired);
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mfaSetupRequired) navigate('/mfa/setup', { replace: true });
  }, [mfaSetupRequired, navigate]);

  // Race au commit de `setAuthenticated` : Zustand et React Router notifient
  // séparément, donc un re-render peut survenir après que `challengeToken`
  // soit null MAIS avant que `navigate('/')` ait commité. Sans cette garde,
  // le guard `!challengeToken` redirigeait vers /login et créait une boucle
  // MFA ↔ login (prod build, cf debug post-étape 19).
  if (accessToken) return <Navigate to="/" replace />;
  if (!challengeToken) return <Navigate to="/login" replace />;

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      setError('Le code doit contenir 6 chiffres.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await authApi.mfaVerify(challengeToken!, code);
      setAuthenticated(res);
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiFailure
          ? err.code === 'UNAUTHORIZED'
            ? 'Code invalide ou session expirée. Reconnectez-vous.'
            : err.message
          : 'Erreur réseau - merci de réessayer',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      title="Vérification 2FA"
      subtitle="Saisissez le code à 6 chiffres affiché dans votre application d'authentification."
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div>
          <Label htmlFor="code" required>
            Code de vérification
          </Label>
          <Input
            id="code"
            name="code"
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className="text-center tracking-[0.4em] font-mono text-lg"
            required
          />
        </div>

        {error && <Banner tone="danger">{error}</Banner>}

        <Button
          type="submit"
          variant="primary"
          className="w-full"
          loading={submitting}
          disabled={code.length !== 6}
        >
          {submitting ? 'Vérification…' : 'Valider'}
        </Button>
      </form>
    </AuthShell>
  );
}
